// React Query key constants. Plan 23 consolidates every per-feature key
// under this single object so invalidations and prefetch targets reference
// the same string tuples without copy-paste drift. Feature code imports
// the narrow sub-object it needs (e.g. `bookingKeys`) instead of this file
// directly — but keeping them co-located avoids circular imports between
// feature folders that want to invalidate each other's caches (e.g. a
// successful booking invalidates `['purchases', 'mine']`).
//
// Rule: never pass literal key arrays around. Always reference a constant.

/** Booking-domain keys (bookings + unavailable slots). */
export const bookingKeys = {
  all: ['bookings'] as const,
  mine: ['bookings', 'mine'] as const,
  unavailable: () => ['bookings', 'unavailable'] as const,
  // Plan 72 — per-booking client Vault resource list (lazy-fetched as each
  // Vault row mounts). Distinct from `adminKeys.bookingResources` so an
  // admin invalidation doesn't clobber the client cache and vice versa.
  resources: (id: number | string) => ['bookings', id, 'resources'] as const,
} as const;

/** Purchases (user-facing + admin). */
export const purchaseKeys = {
  all: ['purchases'] as const,
  mine: ['purchases', 'mine'] as const,
} as const;

/** Services catalog — public list + per-slug detail + admin CRUD (migration 063). */
export const serviceKeys = {
  all: ['services'] as const,
  list: () => ['services'] as const,
  detail: (slug: string) => ['services', 'detail', slug] as const,
  adminList: () => ['admin', 'services'] as const,
} as const;

/** Auth / current user. */
export const authKeys = {
  me: () => ['me'] as const,
  csrfToken: ['csrfToken'] as const,
} as const;

/** Testimonials — both public list and admin moderation view. */
export const testimonialKeys = {
  all: ['testimonials'] as const,
  public: () => ['testimonials'] as const,
  admin: () => ['admin', 'testimonials'] as const,
} as const;

/** Network & Referrals links — public list + admin CRUD (plan 120). */
export const partnerLinkKeys = {
  all: ['partner-links'] as const,
  public: () => ['partner-links'] as const,
  adminList: () => ['admin', 'partner-links'] as const,
} as const;

/** Blog posts — public list/detail + admin CRUD (plan 121). */
export const blogKeys = {
  all: ['blog'] as const,
  public: () => ['blog'] as const,
  publicPost: (slug: string) => ['blog', 'post', slug] as const,
  adminList: () => ['admin', 'blog'] as const,
  adminDetail: (id: number) => ['admin', 'blog', id] as const,
} as const;

/** Content Studio — editorial backlog + kanban (migration 069). */
export const contentIdeaKeys = {
  all: ['content-ideas'] as const,
  adminList: (includeArchived: boolean) => ['admin', 'content-ideas', { includeArchived }] as const,
} as const;

/** Coupon validation (public) + admin CRUD + per-coupon redemptions. */
export const couponKeys = {
  all: ['coupons'] as const,
  adminList: () => ['admin', 'coupons'] as const,
  validate: (code: string) => ['coupons', 'validate', code] as const,
  /**
   * Per-coupon redemption list. Lazy: only mounted when the admin
   * expands a coupon panel, keyed by id so two open panels don't
   * stomp each other's cache.
   */
  redemptions: (id: number) => ['admin', 'coupons', id, 'redemptions'] as const,
} as const;

/** User profile (dashboard + admin profile read). */
export const profileKeys = {
  all: ['userProfile'] as const,
  mine: () => ['userProfile'] as const,
} as const;

/** Questionnaire rows per user (admin). */
export const questionnaireKeys = {
  all: ['questionnaires'] as const,
  byUser: (id: number) => ['admin', 'user', id, 'questionnaires'] as const,
} as const;

/** Admin scope. */
export const adminKeys = {
  users: () => ['admin', 'users'] as const,
  user: (id: number) => ['admin', 'user', id] as const,
  userNotes: (id: number) => ['admin', 'user', id, 'notes'] as const,
  activities: () => ['admin', 'activities'] as const,
  sessions: () => ['admin', 'sessions'] as const,
  // Calendar grid view — keyed by date window so navigating week-to-week
  // doesn't blow away the previous slice's cache.
  calendar: (from: string, to: string) => ['admin', 'calendar', from, to] as const,
  birthDataRequests: () => ['admin', 'birthDataRequests'] as const,
  emailSettings: () => ['admin', 'emailsSettings'] as const,
  // Plan 70 — per-client detail.
  clientContacts: (id: number) => ['admin', 'client', id, 'contacts'] as const,
  clientSessions: (id: number) => ['admin', 'client', id, 'sessions'] as const,
  // Plan 71 — per-booking history (lazy-fetched on Past-tab row expansion)
  // and the booking resources list rendered alongside it.
  bookingHistory: (id: number) => ['admin', 'booking', id, 'history'] as const,
  bookingResources: (id: number) => ['admin', 'booking', id, 'resources'] as const,
  // Plan 78 — admin contact inbox (list + per-thread detail). The list key
  // includes a serialised filter object so different filter shapes get
  // distinct caches. Pass the same shape on read + invalidate; any
  // omitted filter field collapses to the same key.
  contactInbox: {
    all: ['admin', 'contactInbox'] as const,
    list: (params: ContactInboxListParams = {}) =>
      [
        'admin',
        'contactInbox',
        'list',
        {
          status: params.status ?? null,
          inquiry_type: params.inquiry_type ?? null,
          unread: params.unread ?? null,
          existing_client: params.existing_client ?? null,
          q: params.q ?? '',
          limit: params.limit ?? 50,
          offset: params.offset ?? 0,
        },
      ] as const,
    detail: (id: number) => ['admin', 'contactInbox', 'detail', id] as const,
  },
  // Migration 025 — Audio Report deliveries pending admin upload.
  audioReportsPending: () => ['admin', 'audioReports', 'pending'] as const,
  audioReportsDelivered: () => ['admin', 'audioReports', 'delivered'] as const,
  // Plan 122 WS4 — gift certificates ledger, keyed by status.
  giftCertificates: (status: 'pending' | 'fulfilled') =>
    ['admin', 'giftCertificates', status] as const,
  // Plan 208 — Audio Report Builder (NEW surface, distinct from the
  // legacy "Audio Reports" tab above). List key carries the filter shape
  // so different filters get distinct caches; detail key is keyed by id;
  // renderStatus is a sibling so the poller doesn't share a cache with
  // the detail bundle (the bundle stays still while the job ticks).
  audioReportBuilder: {
    all: ['admin', 'audioReportBuilder'] as const,
    list: (params: AudioReportBuilderListParams = {}) =>
      [
        'admin',
        'audioReportBuilder',
        'list',
        {
          status: params.status ?? null,
          search: params.search ?? '',
          page: params.page ?? 1,
          pageSize: params.pageSize ?? 25,
        },
      ] as const,
    detail: (id: number) => ['admin', 'audioReportBuilder', 'detail', id] as const,
    renderStatus: (id: number) => ['admin', 'audioReportBuilder', 'renderStatus', id] as const,
  },
} as const;

/**
 * Plan 208 — params accepted by the admin audio-report-builder list
 * endpoint. Mirrors the server's `ListOrdersQuerySchema` (plan 204).
 */
export interface AudioReportBuilderListParams {
  status?: string | undefined;
  search?: string | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
}

/** Client vault — every CONTENT purchase the buyer owns. */
export const audioReportKeys = {
  mine: () => ['audioReports', 'mine'] as const,
} as const;

/**
 * Plan 78 — params accepted by the admin contact-inbox list endpoint.
 * Mirrors the server's `readStatusQuery` / `readInquiryQuery` filters so
 * the cache key is shaped exactly like the request payload.
 */
export interface ContactInboxListParams {
  status?: string | undefined;
  inquiry_type?: string | undefined;
  unread?: boolean | undefined;
  existing_client?: boolean | undefined;
  q?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

// Legacy flat object. Preserved for call sites that have not migrated yet
// (SessionFeed's useAdmin hooks). New code should import the scoped
// constants above.
export const queryKeys = {
  services: serviceKeys.list,
  me: authKeys.me,
  myBookings: () => bookingKeys.mine,
  myPurchases: () => purchaseKeys.mine,
  testimonials: testimonialKeys.public,
  userProfile: profileKeys.mine,
  birthDataRequests: adminKeys.birthDataRequests,
  adminUsers: adminKeys.users,
  adminUser: adminKeys.user,
  adminUserNotes: adminKeys.userNotes,
  adminUserQuestionnaires: questionnaireKeys.byUser,
  adminActivities: adminKeys.activities,
  adminSessions: adminKeys.sessions,
  adminCoupons: couponKeys.adminList,
  adminTestimonials: testimonialKeys.admin,
  adminEmailsSettings: adminKeys.emailSettings,
  bookingsUnavailable: bookingKeys.unavailable,
} as const;
